/**
 * Probiotic landing page — interactions.
 * Self-contained, scoped to elements inside `.prob-lp`. Covers: buy-box gallery
 * thumbnail swap, sticky CTA visibility, mini-FAQ accordion, 90-day timeline
 * scroll-reveal + line fill, smooth-scroll to the offer, the order-bump toggle,
 * and the real add-to-cart (subscription line + optional one-time upsell) that
 * opens the theme cart drawer via the `cart:refresh` event.
 */
(function () {
  'use strict';
  if (window.__probioticLPInit) return;
  window.__probioticLPInit = true;

  function init() {
    /* ---- Buy-box gallery thumbnail swap (document-delegated) ---- */
    document.addEventListener('click', function (e) {
      var thumb = e.target.closest('[data-prob-thumb]');
      if (thumb) {
        var scope = thumb.closest('.prob-lp') || document;
        var main = scope.querySelector('[data-prob-gallery-img]');
        var src = thumb.getAttribute('data-prob-thumb');
        if (main && src) main.src = src;
        scope.querySelectorAll('[data-prob-thumb]').forEach(function (t) {
          t.classList.remove('active');
        });
        thumb.classList.add('active');
        return;
      }

      /* Mini-FAQ (buy box) — click a row to expand/collapse. */
      var faq = e.target.closest('.pdp-faq-item');
      if (faq) {
        faq.classList.toggle('open');
      }
    });

    /* Keyboard support for the mini-FAQ rows (they're divs, not <summary>). */
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var faq = e.target.closest && e.target.closest('.pdp-faq-item');
      if (faq && (e.target === faq || e.target.classList.contains('pdp-faq-q'))) {
        e.preventDefault();
        faq.classList.toggle('open');
      }
    });

    /* ---- Order-bump upsell: reflect checkbox state on the label for styling ---- */
    document.addEventListener('change', function (e) {
      var check = e.target.closest('[data-prob-upsell-check]');
      if (!check) return;
      var wrap = check.closest('.pdp-upsell');
      if (wrap) wrap.classList.toggle('checked', check.checked);
    });

    /* ---- Smooth-scroll for on-page CTAs pointing at the offer ---- */
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href$="#offer"], a[href="#offer"]');
      if (!link) return;
      var offer = document.getElementById('offer');
      if (offer) {
        e.preventDefault();
        offer.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });

    /* ---- 90-day timeline: reveal items on scroll + fill the line ---- */
    var vtrack = document.querySelector('.prob-lp .tl-vtrack');
    if (vtrack) {
      var fill = vtrack.querySelector('.tl-line-fill');
      var items = vtrack.querySelectorAll('.tl-item');
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (en) {
              if (en.isIntersecting) {
                en.target.classList.add('in-view');
                io.unobserve(en.target);
              }
            });
          },
          { threshold: 0.35 }
        );
        items.forEach(function (el) {
          io.observe(el);
        });
      } else {
        items.forEach(function (el) {
          el.classList.add('in-view');
        });
      }
      var fillLine = function () {
        if (!fill) return;
        var r = vtrack.getBoundingClientRect();
        var prog = (window.innerHeight * 0.5 - r.top) / r.height;
        prog = Math.max(0, Math.min(1, prog));
        fill.style.height = prog * 100 + '%';
      };
      window.addEventListener('scroll', fillLine, { passive: true });
      window.addEventListener('resize', fillLine);
      fillLine();
    }

    /* ---- Sticky CTA bar (hidden until scrolled past the hero, and while the offer is in view) ---- */
    var bar = document.querySelector('.prob-lp .sticky-cta, .sticky-cta');
    var offerSection = document.getElementById('offer');
    if (bar) {
      var onScroll = function () {
        var show = window.scrollY > 700;
        if (offerSection) {
          var r = offerSection.getBoundingClientRect();
          if (r.top < window.innerHeight && r.bottom > 0) show = false;
        }
        bar.classList.toggle('visible', show);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }

    /* ---- Real add-to-cart: subscription line + optional one-time upsell ---- */
    initAddToCart();
  }

  function initAddToCart() {
    // The main buy-box button is the single source of truth for the line-item data
    // (variant / selling plan / subscription property / upsell). The sticky-bar button
    // triggers the same add, so both stay in sync.
    var mainBtn = document.querySelector('[data-prob-atc]');
    if (!mainBtn) return; // no product selected — Liquid rendered a plain link instead
    var box = mainBtn.closest('[data-prob-buybox]') || document;

    var addUrl =
      (window.FoxTheme && window.FoxTheme.routes && window.FoxTheme.routes.cart_add_url) || '/cart/add.js';

    function doAdd(sourceBtn) {
      if (sourceBtn.classList.contains('is-loading')) return;

      var variantId = mainBtn.getAttribute('data-variant-id');
      if (!variantId) return;

      var mainItem = { id: variantId, quantity: 1 };
      var planId = mainBtn.getAttribute('data-selling-plan');
      if (planId) mainItem.selling_plan = planId;
      var subCopy = mainBtn.getAttribute('data-sub-property');
      if (subCopy) mainItem.properties = { Subscription: subCopy };

      var items = [mainItem];

      var upsellCheck = box.querySelector('[data-prob-upsell-check]');
      var upsellId = mainBtn.getAttribute('data-upsell-variant-id');
      if (upsellCheck && upsellCheck.checked && upsellId) {
        items.push({ id: upsellId, quantity: 1 });
      }

      setLoading(sourceBtn, true);
      clearError(box);

      fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items }),
      })
        .then(function (res) {
          return res.json().then(function (data) {
            return { ok: res.ok, data: data };
          });
        })
        .then(function (r) {
          if (!r.ok) {
            throw new Error((r.data && (r.data.description || r.data.message)) || 'Could not add to cart.');
          }
          // Re-render + open the theme cart drawer.
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
          setLoading(sourceBtn, false);
        })
        .catch(function (err) {
          setLoading(sourceBtn, false);
          showError(box, err && err.message ? err.message : 'Could not add to cart. Please try again.');
        });
    }

    mainBtn.addEventListener('click', function (e) {
      e.preventDefault();
      doAdd(mainBtn);
    });

    var stickyBtn = document.querySelector('[data-prob-sticky-atc]');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', function (e) {
        e.preventDefault();
        doAdd(stickyBtn);
      });
    }
  }

  function setLoading(btn, loading) {
    btn.classList.toggle('is-loading', loading);
    if (loading) {
      btn.setAttribute('aria-busy', 'true');
      if (!btn.dataset.label) btn.dataset.label = btn.textContent;
      btn.textContent = 'Adding…';
    } else {
      btn.removeAttribute('aria-busy');
      if (btn.dataset.label) btn.textContent = btn.dataset.label;
    }
  }

  function clearError(box) {
    var el = box.querySelector('[data-prob-atc-error]');
    if (el) {
      el.hidden = true;
      el.textContent = '';
    }
  }
  function showError(box, msg) {
    var el = box.querySelector('[data-prob-atc-error]');
    if (el) {
      el.textContent = msg;
      el.hidden = false;
    } else {
      window.alert(msg);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
